import { useEffect, useState } from 'react';
import Button from '../../common/Button';
import { useTranslation } from 'react-i18next';
import Modal from '../../common/Modal';
import Input from '../../common/Input';
import { setToastsError, setToastsSuccess, setToastsWarning } from '../../../redux/actions/common';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import ChangeRequestBase from './ChangeRequestBase';
import { getNotConfirmTable } from '../../tables/table';
import { ChangeRequestControllerService, PriceSearchRequest } from '../../../services';
import '../../../assets/css/pages/stores/change-request-approve.css';
import apiErrorHandler, { apiValidationErrorHandler } from '../../../api/apiErrorHandler';
import { setLoader } from '../../../redux/actions/api';
import { clearErrors } from '../../../redux/actions/error';
import { discountValidation } from '../../../utils/helpers/validation';
import { setIsContinue } from '../../../redux/actions/modal';
import { setChangeRequestType } from '../../../redux/actions/storage';
import { RequiredBadge } from '../../common/RequiredBadge';

const ChangeRequestApprove = () => {
    // 承認画面
    const confirmStatus = 'NOT_CONFIRM';

    const history = useNavigate();
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const location = useLocation();

    const storage = useSelector((state) => state.storage);
    const { isFreshness, changeRequestType, isCompetitor, isHead } = storage;

    const modal = useSelector((state) => state.modal);
    const { isContinue } = modal;

    useEffect(() => {
        dispatch(clearErrors());
        if (isHead) {
            dispatch(setChangeRequestType(PriceSearchRequest.changeRequestType.FLYER));
        }
    }, [location, dispatch]);

    const [state, setState] = useState({
        apiResponse: null,
        reload: new Date(),

        authorizerName: '',
        isShowModalApprove: false,
        isCheckApproveName: false,
        isPastStartDate: false,
        disabled: false,
        isShowForm: false,
    });
    const [selectedData, setSelectedData] = useState([]);

    const {
        apiResponse,
        reload,

        authorizerName,
        isShowModalApprove,
        isCheckApproveName,
        isPastStartDate,
        disabled,
        isShowForm,
    } = state;

    const handleOnChangeRemandReason = (id, value) => {
        const data = apiResponse?.data.map((item) => {
            if (item.id === id) {
                item.remandReason = value;
            }
            return item;
        });
        setApiResponse({ ...apiResponse, data });
    };

    const table = getNotConfirmTable(t, handleOnChangeRemandReason, isFreshness, isCompetitor, isHead);
    const isNearby = isCompetitor && isShowForm;

    const setApiResponse = (response) => {
        setState((prevState) => ({
            ...prevState,
            apiResponse: response,
        }));
    };

    const setIsShowForm = (bool) => {
        setState((prevState) => ({
            ...prevState,
            isShowForm: bool,
        }));
    };

    const isPastDate = (startDate) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const inputDate = new Date(startDate);

        return inputDate < today;
    };

    const [formData, setFormData] = useState(null);

    useEffect(() => {
        dispatch(clearErrors());
        if (isContinue) {
            dispatch(setIsContinue(false));
            setState((prevState) => ({
                ...prevState,
                isShowModalApprove: true,
            }));
        }
    }, [dispatch, isContinue, location]);

    const handleShowModal = async () => {
        dispatch(clearErrors());
        if (selectedData.length !== apiResponse.totalCount && !isHead) {
            // すべてのデータがチェックされていない場合はエラーを表示
            dispatch(setToastsError(t('message.info.notify.confirm.registration')));
            return;
        }
        dispatch(setLoader(true));
        // 開始日が前日以前の場合、ポップアップのタイトルを変更
        setState((prevState) => ({
            ...prevState,
            isPastStartDate: selectedData.some((item) => isPastDate(item.salesStartDate)),
        }));

        if (!isHead) {
            const checkData = selectedData.filter((item) => item.confirmed && item.nextDayFlag);

            if (checkData.length > 0) {
                const ids = checkData.map((item) => {
                    return {
                        changeRequestId: item.id,
                    };
                });

                try {
                    await ChangeRequestControllerService.validateUpdateChangeRequests({ data: ids });
                } catch (error) {
                    const items = checkData.filter((item) => error.body?.errors[0]?.data[0]?.jan === item.jan)[0];
                    dispatch(setLoader(false));
                    apiValidationErrorHandler(dispatch, error, [{ ...items }]);
                    return;
                }
            }

            // 乖離チェック
            if (await discountValidation(dispatch, checkData, isFreshness)) {
                dispatch(setLoader(false));
                return;
            }
        }

        setState((prevState) => ({
            ...prevState,
            isShowModalApprove: true,
        }));
        dispatch(setLoader(false));
    };

    const handleSubmitModalApprove = () => {
        dispatch(setLoader(true));
        dispatch(clearErrors());
        const request = {
            changeRequestType: changeRequestType,
            authorizerName: authorizerName,
            changeRequestApproveRequests: selectedData.map((item) => {
                return {
                    id: item.id,
                    remandFlg: item.remand,
                    remandReason: item.remandReason,
                    prohibited: item.prohibited,
                    lowerLimit: item.lowerLimit,
                    exception: item.exception,
                };
            }),
        };

        const remandData = selectedData
            .filter((item) => item.remand)
            .map((item) => {
                return item;
            });

        ChangeRequestControllerService.approve(request)
            .then((response) => {
                setState((prevState) => ({
                    ...prevState,
                    isShowModalApprove: false,
                }));
                if (remandData.length > 0 && !isHead) {
                    history('/change-request/remand');
                } else {
                    handleChangeInput('reload', new Date());
                }
                setSelectedData([]);
                dispatch(setLoader(false));
                response.length === 0
                    ? dispatch(setToastsWarning('承認が完了しました。\n連携対象商品はありませんでした。'))
                    : dispatch(setToastsSuccess('承認が完了しました。'));
                // 画面表示後、承認成功時にのみメールを送る
                sendApprovedMail(response);
            })
            .catch((error) => {
                dispatch(setLoader(false));
                apiValidationErrorHandler(dispatch, error, selectedData, '承認が失敗しました。');
            });
    };

    /**
     * 承認完了後の通知メールを送信する
     * 空配列の場合は送信処理を行わない
     *
     * @param {number[]} ids 承認APIのレスポンスで返却される承認対象IDの配列
     */
    const sendApprovedMail = (ids) => {
        if (!ids?.length) return;
        const body = {
            changeRequestType: changeRequestType,
            approvedIds: ids,
        };

        ChangeRequestControllerService.sendApprovedMail(body).catch((error) => {
            apiErrorHandler(dispatch, error);
        });
    };

    const handleCloseModalApprove = () => {
        setState((prevState) => ({
            ...prevState,
            isShowModalApprove: false,
            authorizerName: '',
        }));
    };

    const handleChangeInput = (field, value) => {
        setState((prevState) => ({
            ...prevState,
            [field]: value,
            ...(field === 'authorizerName' && { isCheckApproveName: true }),
        }));
    };

    /**
     * 承認する商品数により注意喚起の文言をポップアップに追記する
     */
    const checkCountSelectedData = () => {
        const count = selectedData.length;

        if (count < 300) {
            // 300件未満
            return null; // 非表示
        } else if (count < 400) {
            // 300件以上400件未満
            return t('confirm_registration_price.modal_confirm.300_count_data_text');
        } else if (count < 600) {
            // 400件以上600件未満
            return t('confirm_registration_price.modal_confirm.400_count_data_text');
        } else {
            // 600件以上
            return t('confirm_registration_price.modal_confirm.600_count_data_text');
        }
    };

    useEffect(() => {
        handleChangeInput('disabled', isHead ? selectedData.length === 0 : apiResponse?.data.length === 0);
    }, [selectedData]);

    return (
        <div className="confirm-registration-competitive">
            <div className="okc-inner">
                <ChangeRequestBase
                    table={table}
                    confirmStatus={confirmStatus}
                    apiResponse={apiResponse}
                    setApiResponse={setApiResponse}
                    formData={formData}
                    setFormData={setFormData}
                    reload={reload}
                    selectedData={selectedData}
                    setSelectedData={setSelectedData}
                    isShowForm={isShowForm}
                    setIsShowForm={setIsShowForm}
                    isNearby={isNearby}
                />
                <div className="d-flex justify-content-end c-slide-top mb-5">
                    <Button
                        className={`btn btn--primary btn-block ${disabled ? 'btn--disabled' : ''}`}
                        onClick={handleShowModal}
                        children={<span className="btn-text">{t('button.admit')}</span>}
                        disabled={disabled}
                    />
                </div>

                <Modal
                    size="sm"
                    isShow={isShowModalApprove}
                    onClose={handleCloseModalApprove}
                    isShowIconClose="true"
                    title={
                        isPastStartDate
                            ? t('confirm_registration_price.modal_confirm_start_date.title')
                            : t('confirm_registration_price.modal_confirm.title')
                    }
                    className="registration-edit-modal"
                    btnList={[
                        {
                            name: isPastStartDate
                                ? t('confirm_registration_price.modal_confirm_start_date.button_ok')
                                : t('confirm_registration_price.modal_confirm.button_ok'),
                            className: 'btn--primary',
                            onClick: () => handleSubmitModalApprove(),
                            disabled: !authorizerName,
                        },
                        {
                            name: isPastStartDate
                                ? t('confirm_registration_price.modal_confirm_start_date.button_return')
                                : t('confirm_registration_price.modal_confirm.button_cancel'),
                            className: 'btn--secondary',
                            onClick: () => handleCloseModalApprove(),
                            disabled: false,
                        },
                    ]}
                >
                    <p className="text-center">
                        {isPastStartDate
                            ? t('confirm_registration_price.modal_confirm_start_date.text_01')
                            : isHead
                              ? t('confirm_registration_price.modal_confirm.flyer_first_content')
                              : t('confirm_registration_price.modal_confirm.first_content')}
                    </p>
                    <p className="text-center">
                        {isPastStartDate
                            ? t('confirm_registration_price.modal_confirm_start_date.text_02')
                            : isHead
                              ? t('confirm_registration_price.modal_confirm.flyer_second_content')
                              : t('confirm_registration_price.modal_confirm.second_content')}
                    </p>
                    <p className="text-center">
                        {isPastStartDate
                            ? '差戻にチェックを付け、差戻・編集画面より修正をお願いします。'
                            : isHead
                              ? ''
                              : '再度内容ご確認・修正をお願いします。'}
                    </p>
                    <p className="text-center okc-error">{checkCountSelectedData()}</p>
                    <div className="approval-name">
                        <div className="row">
                            <span className="approval-required-badge-span">
                                <RequiredBadge />
                            </span>
                            <Input
                                className="form-control form-control--input"
                                name="authorizerName"
                                type="text"
                                autocomplete="off"
                                maxLength="45"
                                placeholder={t('confirm_registration_price.modal_confirm_deleted_csv.approval_name.label')}
                                value={authorizerName}
                                onChange={(value) => handleChangeInput('authorizerName', value)}
                                style={{
                                    border: !authorizerName && isCheckApproveName ? '1px solid #eb2013 !important' : '',
                                    borderColor: !authorizerName && isCheckApproveName ? '#eb2013' : '',
                                }}
                            />
                            {!authorizerName && isCheckApproveName && (
                                <p className="okc-error mt-2 mb-0">
                                    {t('confirm_registration_price.modal_confirm_deleted_csv.approval_name.error_message')}
                                </p>
                            )}
                        </div>
                    </div>
                </Modal>
            </div>
        </div>
    );
};

export default ChangeRequestApprove;
